import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { i18n, translateError } from '@kostbar/i18n'
import type {
  ApiClientSecret,
  CreateApiClientBody,
  UpdateApiClientBody,
} from '@/api/endpoints/api-clients'
import { apiClientsApi } from '@/api/endpoints/api-clients'
import { ApiError } from '@/api/types'

const QUERY_KEY = ['api-clients'] as const

/** Server codes this page explains in its own words; anything else falls back to the shared table. */
const KNOWN_ERRORS = new Set([
  'name_taken',
  'name_required',
  'name_too_long',
  'scopes_required',
  'unknown_scope',
  'token_limit_reached',
  'api_client_not_found',
  'api_client_token_not_found',
])

function showError(error: unknown) {
  if (error instanceof ApiError) {
    const code = error.code ?? ''
    toast.error(
      KNOWN_ERRORS.has(code)
        ? i18n.t(`integrations:errors.${code}`)
        : translateError(error.code, error.status),
    )
    return
  }
  toast.error(i18n.t('integrations:errors.generic'))
}

export function useApiClients(enabled = true) {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: apiClientsApi.list,
    enabled,
  })
}

/**
 * Mutations that return a secret hand it to `onSecret` and then `reset()` themselves, so the
 * plaintext token never lingers in the react-query mutation cache. The list is refetched instead
 * of patched from the response: the list payload carries no secret by construction.
 */
function useSecretMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<ApiClientSecret>,
  onSecret: (secret: ApiClientSecret) => void,
) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn,
    onSuccess: (secret) => {
      onSecret(secret)
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: showError,
    onSettled: () => {
      // Deferred so react-query finishes publishing the result before it is dropped.
      setTimeout(() => mutation.reset(), 0)
    },
  })
  return mutation
}

export function useCreateApiClient(
  onSecret: (secret: ApiClientSecret) => void,
) {
  return useSecretMutation(
    (body: CreateApiClientBody) => apiClientsApi.create(body),
    onSecret,
  )
}

export function useIssueApiClientToken(
  onSecret: (secret: ApiClientSecret) => void,
) {
  return useSecretMutation(
    (clientId: number) => apiClientsApi.issueToken(clientId),
    onSecret,
  )
}

export function useUpdateApiClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateApiClientBody }) =>
      apiClientsApi.update(id, body),
    onSuccess: (client) => {
      toast.success(
        i18n.t(
          client.active
            ? 'integrations:toast.activated'
            : 'integrations:toast.deactivated',
          { name: client.name },
        ),
      )
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: showError,
  })
}

export function useRevokeApiClientToken() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      clientId,
      tokenId,
    }: {
      clientId: number
      tokenId: number
    }) => apiClientsApi.revokeToken(clientId, tokenId),
    onSuccess: () => {
      toast.success(i18n.t('integrations:toast.revoked'))
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: showError,
  })
}
